/**
 * Premium room branding helpers (FE).
 * Spec: docs/ROOM_BRANDING_SPEC.md
 */

import type { RoomBranding } from '../types/game';
import { applyPackBackground } from './packBackground';

export type { RoomBranding };

const HEX = /^#[0-9A-Fa-f]{6}$/;

/** Keep in sync with `be/src/room/branding.ts` BRANDING_MAX_COLORS. */
export const BRANDING_MAX_COLORS = 3;

export function hexToRgbChannels(hex: string): string | null {
  if (!HEX.test(hex)) return null;
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `${r}, ${g}, ${b}`;
}

function applyColorSlot(
  root: HTMLElement,
  hex: string | undefined,
  cssVar: string,
  rgbVar: string
): void {
  if (hex && HEX.test(hex)) {
    const rgb = hexToRgbChannels(hex);
    if (rgb) {
      root.style.setProperty(cssVar, hex);
      root.style.setProperty(rgbVar, rgb);
      return;
    }
  }
  root.style.removeProperty(cssVar);
  root.style.removeProperty(rgbVar);
}

/**
 * Apply branding palette (up to 3): primary, secondary, tertiary.
 * - `--color-*` — pack/lobby/quiz tokens (restore pack theme when cleared)
 * - `--brand-*` — optional overrides for game screens that keep free-room
 *   hex fallbacks via `makeGameTheme` (`var(--brand-primary, #EC4899)` …)
 */
export function applyRoomBrandingColors(branding: RoomBranding | null | undefined): void {
  const root = document.documentElement;
  const colors = (branding?.colors ?? []).slice(0, BRANDING_MAX_COLORS);
  applyColorSlot(root, colors[0], '--color-primary', '--color-primary-rgb');
  applyColorSlot(root, colors[1], '--color-secondary', '--color-secondary-rgb');
  applyColorSlot(root, colors[2], '--color-tertiary', '--color-tertiary-rgb');
  applyColorSlot(root, colors[0], '--brand-primary', '--brand-primary-rgb');
  applyColorSlot(root, colors[1], '--brand-secondary', '--brand-secondary-rgb');
  applyColorSlot(root, colors[2], '--brand-tertiary', '--brand-tertiary-rgb');
}

export function clearRoomBrandingColors(): void {
  applyRoomBrandingColors(null);
}

/**
 * Override pack backdrop with room background, or restore pack image.
 * Sets `data-room-branding-bg` so player screens also show the custom backdrop.
 */
export function applyRoomBrandingBackground(branding: RoomBranding | null | undefined): void {
  const root = document.documentElement;
  const bg = branding?.backgroundDataUrl;
  if (bg && bg.startsWith('data:image/')) {
    root.dataset.roomBrandingBg = '1';
    root.style.setProperty('--pack-bg-image', `url("${bg}")`);
    return;
  }
  delete root.dataset.roomBrandingBg;
  applyPackBackground();
}

export function clearRoomBrandingBackground(): void {
  applyRoomBrandingBackground(null);
}

const MAX_LOGO_EDGE = 256;
const MAX_LOGO_CHARS = 200_000;
const MAX_BACKGROUND_EDGE = 1600;
const MAX_BACKGROUND_CHARS = 500_000;

type ImageResizeOpts = {
  maxEdge: number;
  maxChars: number;
};

function fileToBrandingImageDataUrl(file: File, opts: ImageResizeOpts): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('NOT_IMAGE'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('READ_FAILED'));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, opts.maxEdge / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('CANVAS'));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        const preferPng = file.type === 'image/png' || file.type === 'image/webp';
        let dataUrl = canvas.toDataURL(preferPng ? 'image/png' : 'image/jpeg', 0.85);
        if (dataUrl.length > opts.maxChars) {
          dataUrl = canvas.toDataURL('image/jpeg', 0.72);
        }
        if (dataUrl.length > opts.maxChars) {
          dataUrl = canvas.toDataURL('image/jpeg', 0.55);
        }
        if (dataUrl.length > opts.maxChars) {
          reject(new Error('TOO_LARGE'));
          return;
        }
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('DECODE_FAILED'));
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

/** Resize/compress an image file to a data URL suitable for room logo. */
export function fileToBrandingLogoDataUrl(file: File): Promise<string> {
  return fileToBrandingImageDataUrl(file, {
    maxEdge: MAX_LOGO_EDGE,
    maxChars: MAX_LOGO_CHARS,
  });
}

/** Resize/compress an image for room backdrop (larger than logo). */
export function fileToBrandingBackgroundDataUrl(file: File): Promise<string> {
  return fileToBrandingImageDataUrl(file, {
    maxEdge: MAX_BACKGROUND_EDGE,
    maxChars: MAX_BACKGROUND_CHARS,
  });
}

/**
 * Full branding snapshot for save — every key present so merges can clear fields.
 * Empty string / empty array / false clear the corresponding server field.
 */
export function buildBrandingSavePayload(form: {
  displayName: string;
  colors: string[];
  website: string;
  allowWebsiteCrawl: boolean;
  logoDataUrl?: string;
  backgroundDataUrl?: string;
}): RoomBranding {
  let website = form.website.trim();
  if (website && !/^https?:\/\//i.test(website)) {
    website = `https://${website}`;
  }
  return {
    displayName: form.displayName.trim(),
    colors: form.colors.slice(0, BRANDING_MAX_COLORS),
    website,
    allowWebsiteCrawl: form.allowWebsiteCrawl,
    logoDataUrl: form.logoDataUrl ?? '',
    backgroundDataUrl: form.backgroundDataUrl ?? '',
  };
}
